import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, of, catchError
} from "rxjs";
import { map } from "rxjs/operators";
import { StringUtil } from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { ProductApiClientService } from "@shopliftr/common-ng";

/**
 * Autocomplete controller service for brand selection.
 *
 * This service implements the IAutocompleteController interface to provide
 * autocomplete functionality for brand strings using the ProductApiClientService.
 * Unlike enum-based controllers, this service works with dynamic string values
 * retrieved from the brand suggest endpoint.
 */
@Injectable({
    providedIn: "root"
})
export class BrandAutocompleteControllerService implements IAutocompleteController<string> {
    private readonly _productClient = inject(ProductApiClientService);

    /**
     * Gets autocomplete results for brands based on the provided query.
     *
     * @param query - The search query string to filter brands
     * @param options - Additional autocomplete options (currently unused for brands)
     * @returns Observable of autocomplete results containing brand strings
     */
    autocomplete$(query?: string, options?: any): Observable<IAutocompleteResults<string>> {
        // If query is empty or undefined, return empty results (brands require search text)
        if (StringUtil.emptyString(query)) {
            return of({
                options: []
            });
        }

        // Call the ProductApiClientService to get brands
        return this._productClient.getBrands$(StringUtil.sanitizeString(query)).pipe(
            map((brands: Array<string>) => {
                // Convert brand strings to autocomplete options
                const autocompleteOptions: Array<IAutocompleteOption<string>> = brands.map((brand) => ({
                    option: brand,
                    value: brand
                }));

                return {
                    options: autocompleteOptions
                };
            }),
            catchError(() => {
                // Return empty results on error
                return of({
                    options: []
                });
            })
        );
    }

    /**
     * Gets the display name for a brand value.
     * For brands, the value and display name are the same.
     *
     * @param value - The brand string value
     * @returns The display name for the brand
     */
    getAutocompleteDisplayName(value?: string): string {
        return value || "";
    }

    /**
     * Gets the identifier for tracking purposes in autocomplete lists.
     * Uses the brand string as the unique identifier.
     *
     * @param index - The index of the item in the list
     * @param result - The autocomplete option result
     * @returns The unique identifier for the brand
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<string>): string | undefined {
        return result?.value;
    }

    /**
     * Determines if the brand is unique compared to the brands in the array
     *
     * @param value - The brand to check
     * @param items - Array of existing brands
     * @returns True if the brand is not in the items array, false otherwise
     */
    isUnique(value: string, items: Array<string>): boolean {
        return !items?.includes(value);
    }
}
